import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

/**
 * 軽量 Toast 通知システム。
 *
 * 設計方針:
 *   - 外部ライブラリ無し（react-hot-toast / sonner 等を使わない）。本体側のバンドルを膨らませない
 *   - 1 コンテキスト + 1 Provider + useToast フック
 *   - 一定時間で自動消滅、× で手動消滅
 *   - 種別: success / error / info（情報量はメッセージで補う）
 */

type ToastKind = 'success' | 'error' | 'info'

type Toast = {
  id: string
  kind: ToastKind
  message: string
}

type ToastContextValue = {
  /**
   * Toast を表示する。
   * @param message 本文
   * @param opts 種別など
   */
  show: (message: string, opts?: { kind?: ToastKind; durationMs?: number }) => void
  /** ショートカット */
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const DEFAULT_DURATION_MS = 3500

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const show = useCallback<ToastContextValue['show']>((message, opts) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    const kind = opts?.kind ?? 'info'
    setToasts((prev) => [...prev, { id, kind, message }])
    const duration = opts?.durationMs ?? DEFAULT_DURATION_MS
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
      }, duration)
    }
  }, [])

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (m) => show(m, { kind: 'success' }),
      error: (m) => show(m, { kind: 'error' }),
      info: (m) => show(m, { kind: 'info' }),
    }),
    [show],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast は ToastProvider 内で使ってください')
  return ctx
}

// ===========================================================================
// Viewport
// ===========================================================================

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[]
  onDismiss: (id: string) => void
}) {
  if (toasts.length === 0) return null

  return (
    <div
      className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 max-w-sm w-[calc(100vw-3rem)]"
      role="region"
      aria-label="通知"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  )
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast
  onDismiss: () => void
}) {
  // 入場アニメーション用の mounted フラグ
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 10)
    return () => clearTimeout(t)
  }, [])

  const config: Record<ToastKind, { icon: string; className: string; iconLabel: string }> = {
    success: {
      icon: '✓',
      iconLabel: '成功',
      className:
        'border-emerald-300 bg-emerald-50 text-emerald-900 [&_.toast-icon]:bg-emerald-500 [&_.toast-icon]:text-white',
    },
    error: {
      icon: '!',
      iconLabel: '失敗',
      className:
        'border-destructive/40 bg-destructive/5 text-destructive [&_.toast-icon]:bg-destructive [&_.toast-icon]:text-destructive-foreground',
    },
    info: {
      icon: 'i',
      iconLabel: '情報',
      className:
        'border-border bg-card text-foreground [&_.toast-icon]:bg-foreground [&_.toast-icon]:text-background',
    },
  }
  const c = config[toast.kind]

  return (
    <div
      role="status"
      aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
      className={`pointer-events-auto border rounded-lg shadow-lg px-4 py-3 flex items-start gap-3 transition-all duration-200 ${c.className} ${
        mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
    >
      <span
        aria-label={c.iconLabel}
        className="toast-icon inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold flex-shrink-0 mt-0.5"
      >
        {c.icon}
      </span>
      <p className="text-sm flex-1 min-w-0 break-words">{toast.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="閉じる"
        className="text-current opacity-50 hover:opacity-100 transition-opacity flex-shrink-0"
      >
        ✕
      </button>
    </div>
  )
}
